import PropTypes from 'prop-types';
import React, { Component } from 'react';
import NavBar from '../../components/NavBar.jsx';
import NewTeamModal from './components/NewTeamModal.jsx';
import { connect } from 'react-redux';
import { getUsableChannels } from 'selectors/channels/index.js';
import {
  GECKO_CONSTS,
  GeckoAvatar,
  GeckoCardTable,
  GeckoCardWrapper,
  GeckoCardHeader,
  GeckoCardBody,
  GeckoToastService,
  GeckoRow,
  GeckoCol,
  GeckoModalService,
  GeckoDialogService,
} from '../../ui';
import GeckoLocation from '../../services/gecko-location';
import './Teams.scss';
import GeckoPermissions from '../../services/gecko-permissions';
import GeckoRequest from 'services/gecko-request';
import GeckoPagination from 'services/gecko-pagination';
import { renderError } from 'services/gecko-error/index.js';
import GeckoFeatures from 'services/gecko-features';

class Team extends Component {
  navLinks = [
    {
      to: '/agents',
      activeClassName: 'active',
      linkText: 'Agents',
    },
    {
      to: '/teams',
      activeClassName: 'active',
      linkText: 'Teams',
    },
  ];

  // Agent Table
  agentCols = [
    {
      key: 'status',
      align: 'left',
      type: GECKO_CONSTS.TABLE_CELL_TYPE_STATUS,
      styles: [
        {
          name: 'Available',
          type: GECKO_CONSTS.STATUS_TYPE_SUCCESS,
          key: 'Available',
        },
        {
          name: 'Unavailable',
          type: GECKO_CONSTS.STATUS_TYPE_WARNING,
          key: 'Unavailable',
        },
        {
          name: 'Offline',
          type: GECKO_CONSTS.STATUS_TYPE_DEFAULT,
          key: 'Offline',
        },
      ],
    },
    {
      key: 'agent_name',
      render: (value) => {
        return (
          <div>
            <GeckoAvatar
              name={value.full_name}
              first_name={value.first_name}
              last_name={value.last_name}
              color={value.color}
              bgImage={value.bgImage}
            />
            <span>&nbsp;{value.full_name}</span>
          </div>
        );
      },
      width: '95%',
    },
    {
      key: 'memberStatus',
      render: (val) => this.getAgentMemberStatusIcon(val),
    },
  ];

  getAgentCrossPlatformStatus(agent) {
    if (agent.presence === 'Online') {
      return agent.web_available || agent.mobile_available
        ? 'Available'
        : 'Unavailable';
    } else {
      return 'Offline';
    }
  }

  getMemberIDs() {
    return this.props.agent_teams
      .filter((membership) => membership.team_id === this.props.team_id)
      .map((membership) => membership.agent_id);
  }

  getAgentRows() {
    return this.getAgents(true).concat(this.getAgents());
  }

  getAgents(isMember = false) {
    const memberIDs = this.getMemberIDs();
    return this.props.agents
      .filter((agent) =>
        isMember
          ? memberIDs.indexOf(agent.id) > -1
          : memberIDs.indexOf(agent.id) < 0
      )
      .map((agent) => {
        return {
          agent_id: agent.id,
          agent_name: {
            full_name: agent.full_name,
            first_name: agent.first_name,
            last_name: agent.last_name,
            color: agent.additional.avatar_colour,
            bgImage: agent.additional.profile_picture_url,
          },
          status: this.getAgentCrossPlatformStatus(agent),
          memberStatus: {
            isMember: isMember,
            agent_id: agent.id,
            agent_name: agent.full_name,
            agent_team_id: isMember
              ? this.props.agent_teams.find(
                  (item) =>
                    item.agent_id === agent.id &&
                    item.team_id === this.props.team_id
                ).id
              : null,
          },
        };
      })
      .sort((a, b) => this.compareAgents(a, b));
  }

  compareAgents(a, b) {
    if (
      a.status === 'Available' &&
      (b.status === 'Unavailable' ||
        b.status === 'Offline' ||
        b.status === null)
    ) {
      return -1;
    }
    if (
      a.status === 'Unavailable' &&
      (b.status === 'Offline' || b.status === null)
    ) {
      return -1;
    }
    if (
      (a.status === 'Unavailable' ||
        a.status === 'Offline' ||
        a.status === null) &&
      b.status === 'Available'
    ) {
      return 1;
    }
    if (
      (a.status === 'Offline' || b.status === null) &&
      b.status === 'Unavailable'
    ) {
      return 1;
    } else {
      if (
        a.agent_name.full_name.toUpperCase() <
        b.agent_name.full_name.toUpperCase()
      ) {
        return -1;
      }
      if (
        a.agent_name.full_name.toUpperCase() >
        b.agent_name.full_name.toUpperCase()
      ) {
        return 1;
      }
      return 0;
    }
  }

  getAgentMemberStatusIcon(memberDets) {
    if (memberDets.isMember) {
      return this.memberIcon(() => this.toggleAgentMembership(memberDets));
    }
    return this.nonMemberIcon(() => this.toggleAgentMembership(memberDets));
  }

  toggleAgentMembership(memberDets) {
    if (GeckoPermissions.memberOf('agent')) {
      return;
    }
    if (memberDets.isMember) {
      GeckoRequest.delete(`/models/agent_teams/${memberDets.agent_team_id}`)
        .then(() => {
          new GeckoToastService().success(
            `${memberDets.agent_name} removed from team`
          );
        })
        .catch((err) => {
          new GeckoToastService().error(
            `Unable to remove team member - ${err}`
          );
        });
    } else {
      GeckoRequest.post(`/models/agent_teams/`, {
        agent_id: memberDets.agent_id,
        team_id: this.props.team_id,
      })
        .then(() => {
          new GeckoToastService().success(
            `${memberDets.agent_name} added to team`
          );
        })
        .catch((err) => {
          new GeckoToastService().error(`Unable to add team member - ${err}`);
        });
    }
  }

  // Channels Table
  channelCols = [
    {
      key: 'channel_name',
      render: (value) => {
        return <div>{value}</div>;
      },
      width: '95%',
    },
    {
      key: 'memberStatus',
      render: (val) => this.getChannelMemberStatusIcon(val),
    },
  ];

  sortChannelsByMemberStatus(a, b) {
    if (a.memberStatus.isMember && !b.memberStatus.isMember) {
      return -1;
    }
    if (!a.memberStatus.isMember && b.memberStatus.isMember) {
      return 1;
    }
    return 0;
  }

  getChannelRows() {
    // this is a bit naff, but an acceptable solution for now
    // Otherwise we'll need to look at adapting the gecko table with CSS solutions
    // which will have a wider imapct
    const truncateName = (name) => {
      const { screenSize } = this.props;
      if (screenSize === 'xl' && name.length > 55) {
        return name.slice(0, 55) + '...';
      }
      if (screenSize === 'lg' && name.length > 45) {
        return name.slice(0, 45) + '...';
      }
      if (screenSize === 'md' && name.length > 30) {
        return name.slice(0, 30) + '...';
      }
      if (screenSize === 'sm' && name.length > 55) {
        return name.slice(0, 55) + '...';
      }
      if (screenSize === 'xs' && name.length > 30) {
        return name.slice(0, 30) + '...';
      }
      return name;
    };

    return this.props.channels
      .map((channel) => {
        return {
          channel_name: truncateName(channel.name),
          memberStatus: {
            isMember: channel.assigned_team_ids?.includes(this.props.team_id),
            channel: channel,
          },
        };
      })
      .sort(this.sortChannelsByMemberStatus);
  }

  getChannelMemberStatusIcon(memberDets) {
    if (memberDets.isMember) {
      return this.memberIcon(() => this.toggleChannelMembership(memberDets));
    }
    return this.nonMemberIcon(() => this.toggleChannelMembership(memberDets));
  }

  toggleChannelMembership(memberDets) {
    if (GeckoPermissions.memberOf('agent')) {
      return; // only supervisors and admins can assign teams to channels
    }
    if (memberDets.isMember) {
      // is member - remove assignment
      const assigned_team_ids = memberDets.channel.assigned_team_ids.filter(
        (team_id) => team_id !== this.props.team_id
      );

      GeckoRequest.post(
        `/channel/${memberDets.channel.id}/assign-teams`,
        assigned_team_ids
      )
        .then(() => {
          new GeckoToastService().success(`Team unassigned from channel`);
        })
        .catch((err) => {
          new GeckoToastService().error(
            `Unable to unassign team from channel - ${err}`
          );
        });
    } else {
      // is not member = add assignment
      const assigned_team_ids = [
        ...(memberDets.channel.assigned_team_ids || []),
        this.props.team_id,
      ];

      GeckoRequest.post(
        `/channel/${memberDets.channel.id}/assign-teams`,
        assigned_team_ids
      )
        .then(() => {
          new GeckoToastService().success(`Team assigned to channel`);
        })
        .catch((err) => {
          new GeckoToastService().error(
            `Unable to assign team to channel - ${err}`
          );
        });
    }
  }

  // Team
  getTeamNames() {
    const currentTeam = this.props.teams.find(
      (team) => team.id === this.props.team_id
    );
    return this.props.teams
      .map((team) => team.name)
      .filter((teamName) => teamName !== currentTeam.name);
  }

  returnBtns() {
    if (GeckoPermissions.memberOf('agent')) {
      return [];
    }
    return [
      {
        name: 'Actions',
        type: 'default',
        align: 'right',
        items: [
          {
            name: 'Edit name',
            handler: () => {
              new GeckoModalService().open(() => {
                return (
                  <NewTeamModal
                    teamNamesList={this.getTeamNames()}
                    existing={this.props.teams.find(
                      (team) => team.id === this.props.team_id
                    )}
                  />
                );
              });
            },
          },
          {
            name: 'Remove',
            handler: () => {
              this.removeTeam();
            },
          },
        ],
      },
    ];
  }

  removeTeam() {
    const team = this.props.teams.find(
      (team) => team.id === this.props.team_id
    );
    return new GeckoDialogService()
      .confirm(`Are you sure you want to remove ${team.name}?`)
      .then((confirm) => {
        if (confirm) {
          GeckoRequest.delete(`/models/teams/${team.id}`)
            .then(() => {
              new GeckoToastService().success('Team removed');
              GeckoLocation.goTo('/teams');
            })
            .catch((err) => {
              renderError(err);
            });
        }
      });
  }

  // Render
  memberIcon = (clickHandler) => {
    return (
      <span
        className="member gecko-btn btn btn-outline-primary icon-only"
        onClick={() => clickHandler()}
      >
        <i className="far fa-check"></i>
        <i className="far fa-times"></i>
      </span>
    );
  };

  nonMemberIcon = (clickHandler) => {
    return (
      <span
        className="non-member gecko-btn btn btn-outline-primary icon-only"
        onClick={() => clickHandler()}
      >
        <i className="far fa-plus"></i>
      </span>
    );
  };

  renderHeader() {
    return <GeckoCardHeader name="Team Profile" btns={this.returnBtns()} />;
  }

  renderBody() {
    if (
      !this.props.teams.filter((team) => team.id === this.props.team_id).length
    ) {
      return null;
    }
    const team = this.props.teams.find(
      (team) => team.id === this.props.team_id
    );
    return (
      <div className="TeamProfile">
        <div className="dp_container">
          <div className="text-center">
            <GeckoAvatar name={`${team.name}`} size="lg" />
          </div>
        </div>
        <div className="profile-info">
          <h1 id="name">{`${team.name}`}</h1>
          <div className="profile-meta">ID: {team.uuid}</div>
        </div>
      </div>
    );
  }

  render() {
    if (
      !this.props.teams.filter((team) => team.id === this.props.team_id).length
    ) {
      return null;
    }
    const team = this.props.teams.find(
      (team) => team.id === this.props.team_id
    );
    const [agentRows, agentPagination] = GeckoPagination.paginate({
      page: this.props.params.agent_page,
      changePage: (newPage = 1) => {
        return GeckoLocation.goTo(
          GeckoLocation.location.pathname,
          Object.assign({}, GeckoLocation.params, { agent_page: newPage })
        );
      },
    })(this.getAgentRows());

    const [channelRows, channelPagination] = GeckoPagination.paginate({
      page: this.props.params.channel_page,
      changePage: (newPage = 1) => {
        return GeckoLocation.goTo(
          GeckoLocation.location.pathname,
          Object.assign({}, GeckoLocation.params, { channel_page: newPage })
        );
      },
    })(this.getChannelRows());

    return (
      <div className="Team">
        <NavBar navLinks={this.navLinks} />
        <div className="content-margin">
          <GeckoRow>
            <GeckoCol width="xs-12 md-6">
              <GeckoCardWrapper>
                {this.renderHeader()}
                <GeckoCardBody padded>{this.renderBody()}</GeckoCardBody>
              </GeckoCardWrapper>
              {GeckoFeatures.can('team-channels') && (
                <GeckoCardTable
                  data-testid="team-channels-table"
                  name={'Channels'}
                  cols={this.channelCols}
                  rows={channelRows}
                  pagination={channelPagination}
                />
              )}
            </GeckoCol>
            <GeckoCol width="xs-12 md-6">
              <GeckoCardTable
                data-testid="team-agents-table"
                name={'Team Members'}
                cols={this.agentCols}
                rows={agentRows}
                pagination={agentPagination}
              />
            </GeckoCol>
          </GeckoRow>
        </div>
      </div>
    );
  }
}

Team.defaultProps = {};

Team.propTypes = {
  team_id: PropTypes.number.isRequired,
};

const mapStateToProps = (state) => {
  return {
    params: state.location.params,
    current_user: state.appState.loggedInUser,
    teams: state.teams,
    agent_teams: state.agent_teams,
    agents: state.visible_agents,
    channels: getUsableChannels(state),
    screenSize: state.appState.screenSize,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(Team);
